/**
 * ReinvertirPfModal — renegociación / reinversión de un plazo fijo ACTIVO.
 *
 * NO es una cancelación anticipada con penalidad. Regla de negocio:
 *   - Cancelación anticipada normal → recalcula el interés del período a la
 *     tasa del ahorro corriente (penalidad).
 *   - Reinversión / renegociación (este flujo) → SIN penalidad: se honra el
 *     interés devengado a la tasa del PF por el período parcial y se consolida
 *     en un PF nuevo, normalmente con tasa preferencial mejorada.
 *
 * Flujo de dos pasos para trazabilidad:
 *   1. "Ver plan" → llama reinvertir_plazo_fijo_admin con p_dry_run = true.
 *      Muestra el interés parcial, el movimiento del ahorro y los asientos que
 *      se van a generar. No toca nada.
 *   2. "Confirmar y ejecutar" → misma RPC con p_dry_run = false. Liquida el PF
 *      viejo (capital + interés parcial → ahorro), abre el PF nuevo debitando
 *      del ahorro, y genera las pólizas contables automáticas.
 */
import { useEffect, useState } from 'react'
import { ArrowRight, Sparkle, Warning, X } from '@phosphor-icons/react'
import { supabase } from '@/lib/supabase'

type PlazoFijoLite = {
  id: string
  asociado_id: string
  capital: number
  tasa_anual: number
  certificado_no: string | null
  cuenta_acreditacion_id: string | null
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type Plan = any

function formatQ(n: number | string): string {
  const num = typeof n === 'string' ? parseFloat(n) : n
  return `Q ${(num || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
}

const inputStyle: React.CSSProperties = {
  display: 'block', width: '100%', padding: '8px 10px',
  border: '1px solid var(--border)', borderRadius: 8, fontSize: 13, background: '#fff',
}

export function ReinvertirPfModal({
  open,
  plazoFijo,
  onClose,
  onReinvested,
}: {
  open: boolean
  plazoFijo: PlazoFijoLite | null
  onClose: () => void
  onReinvested: () => void
}) {
  const [ahorro, setAhorro] = useState<number | null>(null)
  const [nuevoCapital, setNuevoCapital] = useState('')
  const [tasaPct, setTasaPct] = useState('')
  const [plazoDias, setPlazoDias] = useState('365')
  const [capitalizacion, setCapitalizacion] = useState<'MENSUAL' | 'AL_VENCIMIENTO'>('MENSUAL')
  const [renovacion, setRenovacion] = useState<'AUTOMATICA' | 'MANUAL'>('AUTOMATICA')
  const [pagarInteres, setPagarInteres] = useState(true)
  const [motivo, setMotivo] = useState('')

  const [plan, setPlan] = useState<Plan | null>(null)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [exito, setExito] = useState<Plan | null>(null)

  useEffect(() => {
    if (!open || !plazoFijo) return
    setNuevoCapital('')
    setTasaPct(((plazoFijo.tasa_anual ?? 0.12) * 100).toString())
    setPlazoDias('365')
    setCapitalizacion('MENSUAL')
    setRenovacion('AUTOMATICA')
    setPagarInteres(true)
    setMotivo('')
    setPlan(null)
    setError(null)
    setExito(null)
    // Traer saldo de ahorro corriente para prefill y contexto
    ;(async () => {
      const { data } = await supabase
        .from('cuenta_ahorro_corriente')
        .select('saldo')
        .eq('asociado_id', plazoFijo.asociado_id)
        .eq('estado', 'activa')
        .order('abierta_at', { ascending: true })
        .limit(1)
        .maybeSingle()
      const saldo = data ? Number((data as { saldo: number }).saldo) : 0
      setAhorro(saldo)
      // Prefill: capital del PF + ahorro (redondeado hacia abajo al entero)
      setNuevoCapital(String(Math.floor(saldo + Number(plazoFijo.capital))))
    })()
  }, [open, plazoFijo])

  const llamar = async (dryRun: boolean) => {
    if (!plazoFijo) return
    setBusy(true)
    setError(null)
    try {
      const { data, error: rpcErr } = await supabase.rpc('reinvertir_plazo_fijo_admin', {
        p_pf_viejo_id: plazoFijo.id,
        p_nuevo_capital: Number(nuevoCapital),
        p_plazo_dias: Number(plazoDias),
        p_tasa_anual: Number(tasaPct) / 100,
        p_tasa_override_motivo: motivo.trim() || null,
        p_manejo_capitalizacion: capitalizacion,
        p_modalidad_renovacion: renovacion,
        p_pagar_interes_parcial: pagarInteres,
        p_dry_run: dryRun,
      })
      if (rpcErr) {
        setError(rpcErr.message)
        return
      }
      if (dryRun) setPlan(data)
      else setExito(data)
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Error al procesar la reinversión.')
    } finally {
      setBusy(false)
    }
  }

  if (!open || !plazoFijo) return null

  const tasaNum = Number(tasaPct)
  const necesitaMotivo = tasaNum !== 12
  const camposOk =
    Number(nuevoCapital) > 0 &&
    Number(plazoDias) > 0 &&
    tasaNum > 0 &&
    (!necesitaMotivo || motivo.trim().length > 0)

  return (
    <div
      style={{ position: 'fixed', inset: 0, background: 'rgba(17,17,17,0.45)', zIndex: 100,
        display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{ width: '100%', maxWidth: 580, maxHeight: '92vh', background: '#fff',
          borderRadius: 18, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}
      >
        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '18px 20px', borderBottom: '1px solid var(--border)' }}>
          <div>
            <div style={{ fontSize: 11, color: 'var(--brand-500)', fontWeight: 700,
              letterSpacing: 0.4, textTransform: 'uppercase' }}>
              {plazoFijo.certificado_no ?? 'Plazo fijo'} · Renegociación
            </div>
            <div style={{ fontSize: 17, fontWeight: 700, marginTop: 2 }}>
              {exito ? 'Reinversión completada' : 'Reinvertir plazo fijo'}
            </div>
          </div>
          <button onClick={onClose} className="btn btn-ghost" style={{ width: 36, height: 36, padding: 0 }}>
            <X size={16} />
          </button>
        </div>

        {/* Body */}
        <div style={{ padding: 20, overflowY: 'auto', flex: 1, display: 'flex', flexDirection: 'column', gap: 16 }}>
          {exito ? (
            <ExitoCard exito={exito} onDone={onReinvested} />
          ) : (
            <>
              {/* Aviso semántica */}
              <div style={{ display: 'flex', gap: 10, padding: 12, borderRadius: 10,
                background: 'rgba(48,96,160,0.06)', border: '1px solid rgba(48,96,160,0.20)' }}>
                <Warning size={18} weight="duotone" color="#3060A0" />
                <div style={{ fontSize: 11, color: 'var(--n-700)', lineHeight: 1.5 }}>
                  Renegociación <strong>sin penalidad</strong>: se honra el interés del PF por el
                  período parcial y se consolida en un PF nuevo. No es una cancelación anticipada.
                </div>
              </div>

              {/* Contexto */}
              <div style={{ display: 'flex', gap: 10 }}>
                <Kpi label="Capital PF actual" value={formatQ(plazoFijo.capital)} />
                <Kpi label="Ahorro corriente" value={ahorro == null ? '…' : formatQ(ahorro)} />
              </div>

              {/* Campos */}
              <Row>
                <Field label="Capital del PF nuevo (Q)" type="number" value={nuevoCapital} onChange={setNuevoCapital} />
                <Field label="Tasa anual (%)" type="number" value={tasaPct} onChange={setTasaPct} />
              </Row>
              <Row>
                <Field label="Plazo (días)" type="number" value={plazoDias} onChange={setPlazoDias} />
                <div style={{ flex: 1 }}>
                  <Label>Capitalización</Label>
                  <select value={capitalizacion} onChange={(e) => setCapitalizacion(e.target.value as 'MENSUAL' | 'AL_VENCIMIENTO')} style={inputStyle}>
                    <option value="MENSUAL">Mensual (paga a ahorro)</option>
                    <option value="AL_VENCIMIENTO">Al vencimiento</option>
                  </select>
                </div>
              </Row>
              <Row>
                <div style={{ flex: 1 }}>
                  <Label>Renovación</Label>
                  <select value={renovacion} onChange={(e) => setRenovacion(e.target.value as 'AUTOMATICA' | 'MANUAL')} style={inputStyle}>
                    <option value="AUTOMATICA">Automática</option>
                    <option value="MANUAL">Manual</option>
                  </select>
                </div>
                <div style={{ flex: 1, display: 'flex', alignItems: 'flex-end', paddingBottom: 8 }}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, color: 'var(--n-700)', cursor: 'pointer' }}>
                    <input type="checkbox" checked={pagarInteres} onChange={(e) => setPagarInteres(e.target.checked)} />
                    Pagar interés parcial devengado
                  </label>
                </div>
              </Row>
              <div>
                <Label>Motivo de la renegociación {necesitaMotivo ? '(requerido)' : ''}</Label>
                <textarea value={motivo} onChange={(e) => setMotivo(e.target.value)} rows={2}
                  placeholder="Ej. Mejora de tasa 12%→13% y consolidación de ahorro + capital en PF único."
                  style={{ ...inputStyle, resize: 'vertical', fontFamily: 'inherit' }} />
              </div>

              {/* Plan (dry-run) */}
              {plan ? <PlanCard plan={plan} /> : null}

              {error ? (
                <div style={{ padding: 12, borderRadius: 10, background: 'rgba(163,45,45,0.08)',
                  border: '1px solid rgba(163,45,45,0.22)', fontSize: 12, color: '#A32D2D' }}>
                  {error}
                </div>
              ) : null}
            </>
          )}
        </div>

        {/* Footer */}
        {!exito ? (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 10,
            padding: '14px 20px', borderTop: '1px solid var(--border)', background: 'var(--n-50)' }}>
            <button onClick={onClose} className="btn btn-ghost" disabled={busy}>Cancelar</button>
            {!plan ? (
              <button onClick={() => llamar(true)} disabled={!camposOk || busy} className="btn btn-primary"
                style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                {busy ? 'Calculando…' : 'Ver plan'}
              </button>
            ) : (
              <>
                <button onClick={() => setPlan(null)} className="btn btn-ghost" disabled={busy}>Ajustar</button>
                <button onClick={() => llamar(false)} disabled={busy} className="btn btn-primary"
                  style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                  {busy ? 'Ejecutando…' : 'Confirmar y ejecutar'}
                </button>
              </>
            )}
          </div>
        ) : null}
      </div>
    </div>
  )
}

function PlanCard({ plan }: { plan: Plan }) {
  const ip = plan.interes_parcial ?? {}
  const ah = plan.ahorro ?? {}
  const pn = plan.pf_nuevo ?? {}
  const asientos: string[] = plan.asientos_a_generar ?? []
  return (
    <div style={{ borderRadius: 12, border: '1px solid rgba(15,138,87,0.25)', background: 'rgba(15,138,87,0.05)', padding: 14 }}>
      <div style={{ fontSize: 11, fontWeight: 700, color: '#0F8A57', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 10 }}>
        Plan (simulación, no ejecutado)
      </div>
      <Line k={`Interés parcial (${ip.dias ?? 0} días)`} v={`${formatQ(ip.bruto ?? 0)} bruto · ISR ${formatQ(ip.isr ?? 0)} · neto ${formatQ(ip.neto ?? 0)}`} />
      <Line k="Ahorro inicial" v={formatQ(ah.inicial ?? 0)} />
      <Line k="+ interés parcial" v={formatQ(ah.mas_interes_parcial ?? 0)} />
      <Line k="+ capital devuelto" v={formatQ(ah.mas_capital_devuelto ?? 0)} />
      <Line k="− PF nuevo" v={formatQ(ah.menos_pf_nuevo ?? 0)} />
      <Line k="Ahorro final" v={formatQ(ah.final ?? 0)} bold />
      <div style={{ height: 1, background: 'rgba(15,138,87,0.20)', margin: '8px 0' }} />
      <Line k="PF nuevo" v={`${formatQ(pn.capital ?? 0)} · ${((pn.tasa ?? 0) * 100).toFixed(2)}% · vence ${pn.fecha_vencimiento ?? ''}`} />
      <Line k="Rinde (neto)" v={formatQ(pn.neto_total ?? 0)} />
      <div style={{ marginTop: 10, fontSize: 10, color: 'var(--n-600)', lineHeight: 1.5 }}>
        <strong>Asientos a generar:</strong>
        <ul style={{ margin: '4px 0 0', paddingLeft: 16 }}>
          {asientos.map((a, i) => <li key={i}>{a}</li>)}
        </ul>
      </div>
    </div>
  )
}

function ExitoCard({ exito, onDone }: { exito: Plan; onDone: () => void }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: 14, borderRadius: 12, background: 'rgba(15,138,87,0.10)' }}>
        <Sparkle size={22} weight="duotone" color="#0F8A57" />
        <div style={{ flex: 1 }}>
          <div style={{ fontWeight: 600 }}>Reinversión realizada.</div>
          <div style={{ fontSize: 12, color: 'var(--n-700)', marginTop: 2 }}>
            PF nuevo <strong>{exito.pf_nuevo_certificado ?? ''}</strong> activo · pólizas generadas.
          </div>
        </div>
      </div>
      <Line k="PF nuevo" v={`${formatQ(exito?.pf_nuevo?.capital ?? 0)} · ${((exito?.pf_nuevo?.tasa ?? 0) * 100).toFixed(2)}%`} />
      <Line k="Ahorro final" v={formatQ(exito.ahorro_final_real ?? exito?.ahorro?.final ?? 0)} bold />
      <button onClick={onDone} className="btn btn-primary"
        style={{ marginTop: 6, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}>
        Ver plazo fijo <ArrowRight size={13} weight="bold" />
      </button>
    </div>
  )
}

function Row({ children }: { children: React.ReactNode }) {
  return <div style={{ display: 'flex', gap: 10 }}>{children}</div>
}
function Label({ children }: { children: React.ReactNode }) {
  return <label style={{ fontSize: 12, color: 'var(--n-700)', fontWeight: 500, display: 'block', marginBottom: 5 }}>{children}</label>
}
function Field({ label, value, onChange, type = 'text' }: { label: string; value: string; onChange: (v: string) => void; type?: string }) {
  return (
    <div style={{ flex: 1 }}>
      <Label>{label}</Label>
      <input type={type} value={value} onChange={(e) => onChange(e.target.value)} style={inputStyle} />
    </div>
  )
}
function Kpi({ label, value }: { label: string; value: string }) {
  return (
    <div style={{ flex: 1, padding: 12, borderRadius: 10, background: 'var(--surface)', border: '1px solid var(--border)' }}>
      <div style={{ fontSize: 10, color: 'var(--n-500)', textTransform: 'uppercase', letterSpacing: 0.5, fontWeight: 700 }}>{label}</div>
      <div style={{ fontSize: 16, fontWeight: 700, color: 'var(--n-900)', marginTop: 3, fontVariantNumeric: 'tabular-nums' }}>{value}</div>
    </div>
  )
}
function Line({ k, v, bold }: { k: string; v: string; bold?: boolean }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, padding: '3px 0', fontSize: 12 }}>
      <span style={{ color: 'var(--n-600)' }}>{k}</span>
      <span style={{ fontWeight: bold ? 700 : 500, color: 'var(--n-900)', fontVariantNumeric: 'tabular-nums', textAlign: 'right' }}>{v}</span>
    </div>
  )
}
